/**
 * Plausible CE analytics wrapper.
 *
 * - Page views are tracked manually (script.manual.js) so React Router controls
 *   when they fire — one view per navigation, not just on initial load.
 * - Custom events are fired via window.plausible() which Plausible's script exposes.
 * - All calls are no-ops when VITE_PLAUSIBLE_URL / VITE_PLAUSIBLE_SITE_ID are absent,
 *   so analytics is fully optional (dev without a Plausible instance just works).
 */

declare global {
  interface Window {
    plausible?: (
      event: string,
      options?: { props?: Record<string, string | number | boolean>; u?: string },
    ) => void
  }
}

const PLAUSIBLE_URL = import.meta.env.VITE_PLAUSIBLE_URL as string | undefined
const SITE_ID = import.meta.env.VITE_PLAUSIBLE_SITE_ID as string | undefined

const enabled = Boolean(PLAUSIBLE_URL && SITE_ID)
let scriptInjected = false

/**
 * Inject the Plausible tracking script once.
 * Uses script.manual.js so automatic pageview on load is suppressed —
 * we fire pageviews ourselves from the router effect in Layout.
 * Call this from a top-level useEffect in Layout.
 */
export function initPlausible(): void {
  if (!enabled || scriptInjected) return
  scriptInjected = true

  const script = document.createElement('script')
  script.defer = true
  script.dataset.domain = SITE_ID!
  script.src = `${PLAUSIBLE_URL}/js/script.manual.js`
  document.head.appendChild(script)
}

/**
 * Track a page view. Call this on every route change.
 * `path` should be the pathname (e.g. '/decks/123') without query/hash so
 * Plausible aggregates dynamic routes correctly.
 */
export function trackPageView(path?: string): void {
  if (!enabled) return
  window.plausible?.('pageview', path ? { u: `${window.location.origin}${path}` } : undefined)
}

/**
 * Track a custom event.
 *
 * @example
 * trackEvent('deck_created')
 * trackEvent('learning_session_started', { source_type: 'deck' })
 */
export function trackEvent(
  name: string,
  props?: Record<string, string | number | boolean>,
): void {
  if (!enabled) return
  window.plausible?.(name, props ? { props } : undefined)
}
